import React, { useMemo } from 'react';
import { View, Text, TouchableOpacity } from 'react-native';
import { DEVICE_SIZES, useDeviceSize } from 'rn-responsive-styles';
import { AppColors } from '../../utility/AppColors';
import { AppFontStyle } from '../../styles/AppFontStyle';
import { NOTIFICATION_STRINGS } from '../../utility/types/stringsConfig';
import { StackActions, useNavigation } from '@react-navigation/native';
import { navigateToScreen } from "../../utility/NavigationHelper";
import { AppScreens } from '../../screens/Utility/AppScreens';
import Avatar from '../../Components/choose-avatar-components/Avatar';
import SvgImage from '../../Components/choose-avatar-components/SvgImage';
import { BELT_MEDAL_TYPES } from '../../config/notificationConfig';

interface WinnerComponentProps {
  item: any;
  selectedButton: string;
}

export const WinnerComponent: React.FC<WinnerComponentProps> = ({ item, selectedButton }) => {
  const deviceSize = useDeviceSize();
  const navigation = useNavigation();

  // Memoize belt data to prevent unnecessary recalculations
  const beltMedalData = useMemo(() => {
    return BELT_MEDAL_TYPES.map(belt => ({
      ...belt,
      value: item?.details?.stakes?.[belt.id]?.quantity || 0,
    }));
  }, [item]);

  // Filter out belts with zero value
  const filteredBeltMedalType = useMemo(() => {
    return beltMedalData.filter(belt => belt.value > 0);
  }, [beltMedalData]);

  // Responsive dimensions
  const isSmallDevice = deviceSize === DEVICE_SIZES.XS || deviceSize === DEVICE_SIZES.SM;
  
  const dimensions = {
    titleFontSize: isSmallDevice ? AppFontStyle.BOLD_ANYBODY_15 : AppFontStyle.BOLD_ANYBODY_40,
    padding: isSmallDevice ? 4 : 8,
    gap: isSmallDevice ? 4 : 12,
    itemGap: isSmallDevice ? 4 : 8,
    marginBottom: isSmallDevice ? 4 : 0,
    textFontSize: isSmallDevice ? 12 : 14,
    playerFontSize: isSmallDevice ? 8 : 10,
    creditsFontSize: isSmallDevice ? 14 : 18,
    creditsLabelFontSize: isSmallDevice ? 8 : 10,
    beltTextFontSize: isSmallDevice ? 8 : 10,
    beltLabelFontSize: isSmallDevice ? 6 : 8,
    itemMargin: isSmallDevice ? 2 : 4,
    containerPadding: isSmallDevice ? 2 : 4,
  };

  const handleProfilePress = () => {
    navigateToScreen(navigation, AppScreens.MyProfileWebScreen);
  };

  const getProfilePicture = () => {
    const defaultAvatar = 'https://squadify-avatar.s3.eu-west-2.amazonaws.com/cat.svg';
    
    if (selectedButton === "I've Organised") {
      return item?.details?.participantProfiles[0]?.profilePicture || defaultAvatar;
    }
    return item?.details?.participantProfiles[1]?.profilePicture || defaultAvatar;
  };

  return (
    <View style={{ flex: 1, marginTop: 10, paddingHorizontal: 5, flexDirection: 'row', flexWrap: 'wrap' }}>
      {/* Winner/Loser Title */}
      <Text
        style={[
          dimensions.titleFontSize,
          {
            textAlign: 'center',
            color: AppColors.white,
            paddingVertical: dimensions.padding,
            flexBasis: '100%',
          },
        ]}
      >
        {item.actionType === 'stake_lost' || item.actionType === 'event-lost' ? NOTIFICATION_STRINGS.LOOSER : NOTIFICATION_STRINGS.WINNER}
      </Text>

      <View style={{ 
        flexDirection: 'row', 
        gap: dimensions.gap, 
        alignItems: 'center', 
        justifyContent: 'center', 
        flexWrap: 'wrap', 
        width: '100%' 
      }}>
        {/* Player Info Section */}
        <View style={{ 
          flexDirection: 'row', 
          gap: dimensions.itemGap, 
          alignItems: 'center', 
          justifyContent: 'center', 
          marginBottom: dimensions.marginBottom 
        }}>
          {/* One vs One Label */}
          <View style={{ gap: 3, alignItems: 'center' }}>
            <Text style={{
              ...AppFontStyle.BOLD_ANYBODY_14,
              color: AppColors.purple_B155FF,
              textAlign: 'center',
              fontSize: dimensions.textFontSize,
            }}>
              {NOTIFICATION_STRINGS.ONE_VS_ONE}
            </Text>
          </View>

          {/* Player Avatar and Name */}
          <View style={{ gap: 3, alignItems: 'center' }}>
            <View style={{
              padding: dimensions.containerPadding,
              backgroundColor: AppColors.grayColor_282828,
              borderRadius: 2,
            }}>
              <TouchableOpacity onPress={handleProfilePress}>
                <View style={{ overflow: 'hidden' }}>
                  <Avatar
                    src={getProfilePicture()}
                    variant="notificationLargePlayer"
                  />
                </View>
              </TouchableOpacity>
            </View>
            <Text style={{
              ...AppFontStyle.BOLD_ANYBODY_10,
              paddingLeft: 2,
              textDecorationLine: 'underline',
              color: AppColors.white,
              textAlign: 'center',
              fontSize: dimensions.playerFontSize,
            }}>
              {item?.userId || NOTIFICATION_STRINGS.UNKNOWN}
            </Text>
          </View>
        </View>

        {/* Credits and Belts Section */}
        <View style={{ gap: 4, alignItems: 'center' }}>
          {/* Gift Credits */}
          <View style={{ flexDirection: 'row', gap: 8, alignItems: 'center' }}>
            <Text style={[
              AppFontStyle.BOLD_ANYBODY_18,
              { color: AppColors.white, fontSize: dimensions.creditsFontSize }
            ]}>
              {item.giftCredits || 0}
            </Text>
            <Text style={[
              AppFontStyle.BOLD_ANYBODY_10,
              { color: AppColors.white, fontSize: dimensions.creditsLabelFontSize }
            ]}>
              {NOTIFICATION_STRINGS.GIFT_CREDITS}
            </Text>
          </View>

          {/* Belts/Medals Display */}
          <View style={{
            borderColor: AppColors.white_C1C1C1,
            borderWidth: 2,
            borderRadius: 8,
            padding: dimensions.containerPadding === 2 ? 2 : 10,
            backgroundColor: AppColors.gray_5B5B5B,
            flexDirection: 'row',
            gap: dimensions.containerPadding === 2 ? 2 : 8,
            alignSelf: 'center',
            flexWrap: 'wrap',
            justifyContent: 'center',
          }}>
            {filteredBeltMedalType.map((beltItem, index) => (
              <View
                key={index}
                style={{
                  alignItems: 'center',
                  gap: 1,
                  margin: dimensions.itemMargin,
                }}
              >
                <SvgImage
                  src={beltItem.image}
                  variant="notificationBeltMedal"
                />
                <Text style={[
                  AppFontStyle.REGULAR_ANYBODY_10,
                  {
                    color: AppColors.white,
                    textAlign: 'center',
                    fontSize: dimensions.beltTextFontSize,
                  },
                ]}>
                  {beltItem.value}
                </Text>
                <Text style={[
                  AppFontStyle.REGULAR_ANYBODY_8,
                  {
                    color: AppColors.white,
                    textAlign: 'center',
                    fontSize: dimensions.beltLabelFontSize,
                  },
                ]}>
                  {beltItem.title}
                </Text>
              </View>
            ))}
          </View>
        </View>
      </View>
    </View>
  );
};
